
import { Component, Vue } from 'vue-property-decorator'
import keyCodeMapperCharacter from '@/utils/keycodemap.utils'
import * as onScan from 'onscan.js'
import InventoryProvider from '@/resources/inventory-provider.provider'
import StockLocationProvider from '@/resources/stock-location.provider'
import { mapState, mapActions } from 'vuex'

import PickHeader from './components/pick-header.vue'
import PickFooter from './components/pick-footer.vue'
import PickStep from './components/pick-step.vue'
import SummaryStep from './components/summary.vue'

@Component({
  components: {
    PickHeader,
    PickFooter,
    PickStep,
    SummaryStep
  },
  computed: {
    ...mapState({
      selectBranch: (state: any) => state.Store.selectBranch,
      products: (state: any) => state.FastPick.products,
      withdrawNotes: (state: any) => state.FastPick.withdrawNotes,
      orders: (state: any) => state.FastPick.orders,
      currentStep: (state: any) => state.FastPick.currentStep,
      pickPage: (state: any) => state.FastPick.pickPage,
      currentLocation: (state: any) => state.FastPick.currentLocation
    })
  },
  methods: {
    ...mapActions({
      setProduct: 'FastPick/setProduct',
      addProduct: 'FastPick/addProduct',
      removeProduct: 'FastPick/removeProduct',
      setWn: 'FastPick/setWn',
      addWn: 'FastPick/addWn',
      removeWn: 'FastPick/removeWn',
      setCurrentStep: 'FastPick/setCurrentStep',
      setPickPage: 'FastPick/setPickPage',
      setCurrentLocation: 'FastPick/setCurrentLocation',
      setDefault: 'FastPick/setDefault',
      decrementLocationCount: 'FastPick/decrementLocationCount',
      addProductIndexQty: 'FastPick/addProductIndexQty',
      addProductIndexWn: 'FastPick/addProductIndexWn',
      incrementProductIndexCount: 'FastPick/incrementProductIndexCount'
    })
  }
})
export default class PickWn extends Vue {
  // --- STATE ---
  products!: any

  withdrawNotes!: any

  orders!: any

  selectBranch!: any

  currentStep!: any

  pickPage!: any

  currentLocation!: any

  setProduct!: any

  addProduct!: any

  removeProduct!: any

  setWn!: any

  addWn!: any

  removeWn!: any

  setCurrentStep!: any

  setPickPage!: any

  setCurrentLocation!: any

  setDefault!: any

  decrementLocationCount!: any

  addProductIndexQty!: any

  addProductIndexWn!: any

  incrementProductIndexCount!: any

  loadingWn: any = false

  loadingLocation: any = false

  // --- SERVICES (Data Logic) ---
  inventoryService = new InventoryProvider()

  stockLocationService = new StockLocationProvider()

  // --- COMPUTED (State Calculation) ---
  get totalSkuAndTotalQty (): any {
    return {
      totalSku: this.products.length,
      totalQty: this.products.reduce((sum: number, item: any) => sum + item.qty, 0),
      totalCount: this.products.reduce((sum: number, item: any) => sum + item.count, 0)
    }
  }

  // --- LIFECYCLE / SCANNER LOGIC ---
  async mounted (): Promise<void> {
    if ((this.currentStep === 'start' && this.withdrawNotes.length) || this.orders.length) {
      this.setDefault()
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this
    onScan.attachTo(document, {
        suffixKeyCodes: [13],
        reactToPaste: true,
        onScan (scanCode) {
          const sCode = scanCode.trim()
          const patternUrl = /^(https:\/\/www\.gentlewomanonline\.com\/|https:\/\/gentlewomanonline\.com\/|https:\/\/www\.matter-makers\.com\/|https:\/\/matter-makers\.com\/)/
          const patternWn = /withdraw-note\?id=(\d+)(?:&|#|$)/
          const patternLocation = /^location::/
          if (patternLocation.test(sCode)) {
            _this.getLocation(sCode.split('::')[1])
          } else if (patternUrl.test(sCode.toLowerCase())) {
            const splitCodeFromUrl = sCode.split('/')
            const code = splitCodeFromUrl[splitCodeFromUrl.length - 1]
            if (code) _this.incrementCounter(code)
          } else if (patternWn.test(sCode)) {
            const wnId = sCode.match(patternWn)?.[1] ?? ''
            _this.getWnDetail(wnId)
          } else {
            _this.$buefy.toast.open({ message: 'QR Code นี้ไม่สามารถใช้งานได้', type: 'is-danger' })
          }
        },
        keyCodeMapper (oEvent: any) {
          if (keyCodeMapperCharacter[+oEvent.keyCode] !== undefined) {
            return keyCodeMapperCharacter[+oEvent.keyCode][+oEvent.shiftKey]
          }
          return onScan.decodeKeyEvent(oEvent)
        }
    })
  }

  // eslint-disable-next-line class-methods-use-this
  beforeDestroy (): void {
    onScan.detachFrom(document)
  }

  // --- METHODS (Business Logic/State Manipulation) ---
  async getLocation (locationId: string): Promise<void> {
    if (this.currentStep !== 'pick') {
      this.$buefy.toast.open({ message: 'กรุณาสแกน QR Code ของ WN', type: 'is-danger' })
      return
    }
    try {
      this.loadingLocation = true
      const { data: scanLocation } = await this.stockLocationService.getLocationById(locationId, { warehouse: this.selectBranch.warehouse.id })
      if (!scanLocation) {
        this.$buefy.toast.open({ message: 'ไม่มีตำแหน่งนี้ใน warehouse ที่คุณเลือก', type: 'is-danger' })
        return
      }
      const newLocationIsExistingInMapping = this.products[this.pickPage].locations.findIndex((lo: any) => lo.location.id === scanLocation?.id)
      if (newLocationIsExistingInMapping !== -1) {
        this.setCurrentLocation(scanLocation)
      } else {
        this.$buefy.toast.open({ message: 'ไม่มีสินค้าอยู่ในตำแหน่งนี้', type: 'is-danger' })
      }
    } catch (error) {
      this.$buefy.toast.open({ message: 'การดึงข้อมูล location ผิดพลาด', type: 'is-danger' })
    } finally {
      this.loadingLocation = false
    }
  }

  async getWnDetail (noteId: string): Promise<void> {
    if (this.currentStep !== 'start') {
      this.$buefy.toast.open({ message: 'ไม่สามารถเพิ่ม WN ระหว่างหยิบได้', type: 'is-danger' })
      return
    }

    try {
      this.loadingWn = true
      const { data: wnInfo } = await this.inventoryService.getWithdrawNoteById(noteId)
      if (!wnInfo) {
        this.$buefy.toast.open({ message: 'WN ไม่ถูกต้อง', type: 'is-danger' })
      } else if (wnInfo.warehouse.id !== this.selectBranch.warehouse.id) {
        this.$buefy.toast.open({ message: 'WN นี้ไม่ใช่ของสาขาที่คุณเลือก', type: 'is-danger' })
      } else if (wnInfo.status !== 'pending') {
        this.$buefy.toast.open({ message: 'WN นี้ไม่อยู่ในสถานะที่ทำการ pick ได้', type: 'is-danger' })
      } else if (this.withdrawNotes.length) {
        this.$buefy.toast.open({ message: 'ทำได้ครั้งละ 1 WN เท่านั้น', type: 'is-danger' })
        return
      } else {
        const isDuplicateWn = this.withdrawNotes.some((noteCode: string) => noteCode === wnInfo.code)
        if (isDuplicateWn) {
          this.$buefy.toast.open({ message: 'มี WN นี้อยู่ในรายการแล้ว', type: 'is-danger' })
          return
        }

        const existingDraft = await this.fetchActiveDraft(wnInfo.code)
        if (existingDraft) {
          this.confirmUseExistingDraft(existingDraft)
          return
        }

        this.addWn(wnInfo.code)
        const toSkuWithMeta = (product: any) => product.skus.map((sku: any) => ({ ...sku, photoUrl: product.photoUrl, productName: product.name }))
        const skus = wnInfo.products.flatMap(toSkuWithMeta)

        const newSkus = skus.filter((sku: any) => this.products.findIndex((p: any) => p.code === sku.code) === -1)
        const existingSkus = skus.filter((sku: any) => this.products.findIndex((p: any) => p.code === sku.code) !== -1)

        const locationResults = await Promise.all(newSkus.map((sku: any) => this.getProductLocation(sku.code)))
        newSkus.forEach((sku: any, i: number) => {
          this.addProduct({
            id: sku.id,
            code: sku.code,
            images: sku.photoUrl,
            price: sku.price,
            option: sku.option || '',
            name: sku.productName,
            collection: '',
            size: sku.size,
            color: sku.color,
            qty: sku.amount,
            locations: locationResults[i] ?? [],
            count: 0,
            withdrawNotes: [{ code: wnInfo.code, qty: sku.amount }]
          })
        })
        existingSkus.forEach((sku: any) => {
          const existingProductIndex = this.products.findIndex((p: any) => p.code === sku.code)
          this.addProductIndexQty({ productIndex: existingProductIndex, qty: sku.amount })
          this.addProductIndexWn({ productIndex: existingProductIndex, code: wnInfo.code, qty: sku.amount })
        })
      }
    } catch (error) {
      this.$buefy.toast.open({ message: 'การดึงข้อมูล WN ผิดพลาด', type: 'is-danger' })
    } finally {
      this.loadingWn = false
    }
  }

  async fetchActiveDraft (wnCode: string): Promise<any> {
    try {
      const { data } = await this.stockLocationService.getPickWnDraftByCode(wnCode)
      return data ?? null
    } catch {
      return null
    }
  }

  confirmUseExistingDraft (draft: any): void {
    this.$buefy.dialog.confirm({
      message: 'มี draft การหยิบค้างอยู่ ต้องการใช้ draft เดิมต่อหรือไม่?',
      confirmText: 'ใช้ draft เดิม',
      cancelText: 'เริ่มใหม่',
      type: 'is-warning',
      onConfirm: () => {
        this.loadDraft(draft)
      },
      onCancel: async () => {
        try {
          await this.stockLocationService.deletePickWnDraftByCode(draft.withdrawNotes[0])
        } catch {
          // draft อาจถูกลบไปแล้ว
        }
      }
    })
  }

  loadDraft (draft: any): void {
    this.setWn(draft.withdrawNotes)
    this.setProduct(draft.products)
    this.setCurrentStep(draft.currentStep)
    this.setPickPage(draft.pickPage)
    this.setCurrentLocation(draft.currentLocation)
  }

  incrementCounter (code: string): void {
    if (this.currentStep === 'start') {
      this.$buefy.toast.open({ message: 'กรุณาสแกน QR Code ของ WN', type: 'is-danger' })
    } else if (this.currentStep === 'summary') {
      this.$buefy.toast.open({ message: 'หากต้องการหยิบต่อกรุณากดปุ่ม Back', type: 'is-danger' })
    } else if (!this.currentLocation) {
      this.$buefy.toast.open({ message: 'กรุณาสแกน QR Code ของ ตำแหน่งที่คุณอยู่', type: 'is-danger' })
    } else if (this.products[this.pickPage] && this.products[this.pickPage].code !== code) {
      this.$buefy.toast.open({ message: 'ไม่ใช่รายการสินค้าในหน้านี้', type: 'is-danger' })
    } else if (this.products[this.pickPage].qty === this.products[this.pickPage].count) {
      this.$buefy.toast.open({ message: 'จำนวนสินค้าในหน้านี้ครบแล้วกรุณาตรวจสอบ', type: 'is-danger' })
    } else {
      const locationIndex = this.products[this.pickPage].locations.findIndex((lo: any) => lo.location.id === this.currentLocation?.id)
      if (locationIndex !== -1) {
        if (this.products[this.pickPage].locations[locationIndex].count === this.products[this.pickPage].locations[locationIndex].qty) {
          this.$buefy.toast.open({ message: 'จำนวนสินค้าใน location นี้หมดแล้วกรุณาตรวจสอบ', type: 'is-danger' })
        } else {
          this.incrementProductIndexCount({ productIndex: this.pickPage, productLocationIndex: locationIndex })
        }
      } else {
        this.$buefy.toast.open({ message: 'ไม่มีสินค้าในตำแหน่งนี้', type: 'is-danger' })
      }
    }
  }

  handleDecrement (locationIndex: number): void {
    this.decrementLocationCount({ productIndex: this.pickPage, locationIndex })
  }

  async getProductLocation (code: string): Promise<any> {
    try {
      const { data } = await this.stockLocationService.getProductLocation({
        warehouse: this.selectBranch.warehouse.id,
        code
      })
      return data.map((lo: any) => ({ ...lo, count: 0 }))
    } catch (error) {
      this.$buefy.toast.open({ message: 'การต้นหาข้อมูลตำแหน่งผิดพลาด', type: 'is-danger' })
      return []
    }
  }

  async startPick (): Promise<void> {
    this.setCurrentStep('pick')

    try {
      await this.stockLocationService.createPickWnDraft({
        products: this.products,
        orders: this.orders,
        withdrawNotes: this.withdrawNotes,
        currentStep: 'pick',
        pickPage: 0,
        currentLocation: null,
        warehouse: this.selectBranch.warehouse
      })
    } catch {
      // draft ไม่ได้ block การทำงาน
    }
  }

  deleteWn (code: string): void {
    this.removeWn(code)

    const products = this.products.flatMap((p: any) => {
      const wnQty = p.withdrawNotes.find((wn: any) => wn.code === code)

      if (!wnQty) {
        return [p]
      }
      const newQty = p.qty - wnQty.qty

      if (newQty <= 0) {
        return []
      }
      return [{
        ...p,
        qty: newQty,
        withdrawNotes: p.withdrawNotes.filter((wn: any) => wn.code !== code)
      }]
    })
    this.setProduct(products)
  }

  stockLocationAdjustment (): void {
    const productSkus = this.products.flatMap((product: any) => product.locations.filter((loc: any) => loc.count > 0).map((loc: any) => ({
      code: product.code,
      location: loc.location.id,
      qty: loc.count
    })))
    this.$buefy.dialog.confirm({
      message: 'หยิบเสร็จแล้วใช่หรือไม่',
      canCancel: true,
      cancelText: 'หยิบต่อ',
      confirmText: 'เสร็จ',
      type: 'is-success',
      onConfirm: async () => {
        if (productSkus.length) {
          try {
            await this.stockLocationService.decrementMappingQty({
              warehouse: this.selectBranch.warehouse.id,
              productSkus
            })
            await this.stockLocationService.finishedPickWnDraftByCode(this.withdrawNotes[0], {})
            this.$buefy.toast.open({ message: 'บันทึกสำเร็จ', type: 'is-success' })
            this.setDefault()
          } catch (error) {
            this.$buefy.toast.open({ message: 'เกิดข้อผิดพลาด', type: 'is-danger' })
          }
        } else {
          this.$buefy.toast.open({ message: 'ไม่มีรายการที่สามารถนำไปอัพเดตข้อมูลได้', type: 'is-danger' })
        }
      }
    })
  }

  locationsNowAlsoExistsInLocationsNext (): void {
    const stayingOnNextLocation = this.products[this.pickPage + 1].locations.some((next: any) => next.location.id === this.currentLocation?.id)
    if (!stayingOnNextLocation) {
      this.setCurrentLocation(null)
    }
  }

  locationsNowAlsoExistsInPreviousLocations (): void {
    const stayingOnNextLocation = this.products[this.pickPage - 1]?.locations.some((next: any) => next.location.id === this.currentLocation?.id)
    if (!stayingOnNextLocation) {
      this.setCurrentLocation(null)
    }
  }

  async goNext (): Promise<void> {
    if (this.pickPage < this.products.length - 1) {
      this.locationsNowAlsoExistsInLocationsNext()
      this.setPickPage(this.pickPage + 1)
    } else {
      this.setCurrentLocation(null)
      this.setCurrentStep('summary')
    }
    await this.syncDraft()
  }

  async backPick (): Promise<void> {
    if (this.currentStep === 'summary') {
      this.setPickPage(this.products.length - 1)
      this.setCurrentStep('pick')
    } else if (this.pickPage > 0) {
      this.locationsNowAlsoExistsInPreviousLocations()
      this.setPickPage(this.pickPage - 1)
    }
    await this.syncDraft()
  }

  async syncDraft (): Promise<void> {
    if (!this.withdrawNotes.length) return
    try {
      await this.stockLocationService.updatePickWnDraftByCode(this.withdrawNotes[0], {
        products: this.products,
        currentStep: this.currentStep,
        pickPage: this.pickPage,
        currentLocation: this.currentLocation
      })
    } catch {
      // ไม่ block การทำงาน
    }
  }

  async onCancel (): Promise<void> {
    if (this.currentStep === 'pick' && this.withdrawNotes.length) {
      try {
        await this.stockLocationService.deletePickWnDraftByCode(this.withdrawNotes[0])
      } catch {
        // ไม่ block การทำงาน
      }
    }
    this.setDefault()
  }
}
